// src/components/statistics/UserStatsModal.tsx

import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./UserStatsModal.css";
import { ModalPortal } from "../common/ModalPortal";
import { statisticsService, UserRanking } from "../../services/statisticsService";
import { FaSyncAlt, FaTimes, FaChartBar } from "react-icons/fa";

/**
 * Session-level cache for the ranking data. The old UserStatsBar fired
 * GET /api/statistics/my-ranking on every page load; the modal fetches it
 * only when first opened and reuses the cached result on later opens.
 * The refresh button bypasses the cache. Cleared on full page reload.
 */
let cachedRanking: UserRanking | null = null;

interface UserStatsModalProps {
  onClose: () => void;
}

/**
 * Centered modal showing the personal stats that used to live in the
 * full-width UserStatsBar: entry count, entry rank, karma and karma rank.
 * Opened from the trophy/chart button in the header (authenticated users only).
 */
export const UserStatsModal: React.FC<UserStatsModalProps> = ({ onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [ranking, setRanking] = useState<UserRanking | null>(cachedRanking);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [spinning, setSpinning] = useState(false);

  const load = useCallback(async (force = false) => {
    if (!force && cachedRanking) {
      setRanking(cachedRanking);
      return;
    }
    setLoading(true);
    setError(false);
    try {
      const result = await statisticsService.getUserRanking();
      cachedRanking = result;
      setRanking(result);
    } catch {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch on first open — later opens hit the session cache (no extra call)
  useEffect(() => {
    load();
  }, [load]);

  // Close on Escape
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  const handleRefresh = async () => {
    setSpinning(true);
    await load(true);
    setTimeout(() => setSpinning(false), 600);
  };

  const handleAllStats = () => {
    onClose();
    navigate("/istatistikler");
  };

  const fmt = (n: number) => n.toLocaleString("tr-TR");

  return (
    <ModalPortal>
      <div
        className="user-stats-overlay"
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        <div className="user-stats-modal">

          <div className="user-stats-modal__header">
            <span className="user-stats-modal__title">{t("stats_modal_title", "İstatistiklerim")}</span>
            <div className="user-stats-modal__header-actions">
              <button
                className={`user-stats-modal__refresh ${spinning ? "spinning" : ""}`}
                onClick={handleRefresh}
                title={t("refresh_btn", "Yenile")}
                disabled={loading}
              >
                <FaSyncAlt size={12} />
              </button>
              <button
                className="user-stats-modal__close"
                onClick={onClose}
                aria-label={t("admin_cancel", "İptal")}
              >
                <FaTimes size={14} />
              </button>
            </div>
          </div>

          <div className="user-stats-modal__body">
            {loading && !ranking && (
              <div className="user-stats-modal__loading">{t("load_more_loading", "yükleniyor...")}</div>
            )}

            {error && !ranking && (
              <div className="user-stats-modal__error">{t("stats_modal_error", "İstatistikler yüklenemedi")}</div>
            )}

            {ranking && (
              <div className="user-stats-modal__metrics">
                {/* Entry count */}
                <div className="user-stats-modal__metric">
                  <span className="user-stats-modal__icon">✍️</span>
                  <span className="user-stats-modal__label">{t("stats_modal_entry_count", "Tanım")}</span>
                  <span className="user-stats-modal__value">{fmt(ranking.entryCount)}</span>
                </div>

                {/* Entry rank */}
                <div className="user-stats-modal__metric">
                  <span className="user-stats-modal__icon">🏆</span>
                  <span className="user-stats-modal__label">{t("stats_modal_entry_rank", "Tanım sırası")}</span>
                  <span className="user-stats-modal__value">
                    #{fmt(ranking.entryRank)}
                    <span className="user-stats-modal__total"> / {fmt(ranking.totalUsers)}</span>
                  </span>
                </div>

                {/* Karma */}
                <div className="user-stats-modal__metric">
                  <span className="user-stats-modal__icon">⚡</span>
                  <span className="user-stats-modal__label">{t("stats_modal_karma", "Karma")}</span>
                  <span className={`user-stats-modal__value${ranking.karma < 0 ? " user-stats-modal__value--negative" : ""}`}>
                    {ranking.karma > 0 ? `+${fmt(ranking.karma)}` : fmt(ranking.karma)}
                  </span>
                </div>

                {/* Karma rank */}
                <div className="user-stats-modal__metric">
                  <span className="user-stats-modal__icon">⭐</span>
                  <span className="user-stats-modal__label">{t("stats_modal_karma_rank", "Karma sırası")}</span>
                  <span className="user-stats-modal__value">
                    #{fmt(ranking.karmaRank)}
                    <span className="user-stats-modal__total"> / {fmt(ranking.totalUsers)}</span>
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className="user-stats-modal__footer">
            <button className="user-stats-modal__all-link" onClick={handleAllStats}>
              <FaChartBar size={12} />
              <span>{t("stats_modal_all_link", "Tüm istatistikler")}</span>
            </button>
          </div>

        </div>
      </div>
    </ModalPortal>
  );
};